import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, FlexItem, Title, Text, Grid, GridItem } from '@patternfly/react-core';
import { Dispatch, useEffect, useState } from 'react';
import BreadCrumbs from '../BreadCrumbs';
import { SubNavContext } from '../../helpers/sub-nav-contexts/sub-nav-context';
import SubNavCommonActions from '../../components/subnav/SubNavCommonActions';
import SubNavReportSummary from '../../components/report/SubNavReportSummary';
import SubNavTipsAndTricks from '../../components/subnav/SubNavTipsAndTricks';
import SubNavReportSnapshot from '../../components/report/SubNavReportSnapshot';
import _ from 'lodash';
import { BoolStringEnum } from '../../enums/bool-string.enum';
import { getAccessibleTextColor } from '../../utilities';

type props = {
	pageTitle: string;
	pageDescription?: string | undefined;
	expanded?: boolean | undefined;
	setExpanded?: Dispatch<boolean> | undefined;
	isCollapsable?: boolean | undefined;
	subNavContext?: SubNavContext;
	collapseByDefault?: boolean;
};

function PageTitleSubheader({
	pageTitle,
	pageDescription,
	isCollapsable,
	expanded,
	setExpanded,
	subNavContext,
	collapseByDefault,
}: props) {
	const [isAnalyze, setIsAnalyze] = useState<boolean>(false);
	const [isReports, setIsReports] = useState<boolean>(false);

	const onToggle = () => {
		setExpandedStorageValue(!expanded);
		setExpanded && setExpanded(!expanded);
	};

	useEffect(() => {
		const url = window.location.href;
		setIsAnalyze(false);
		setIsReports(false);

		if (url.includes('/analyze')) {
			setIsAnalyze(true);
		}
		if (url.includes('/report')) {
			setIsReports(true);
		}

		if (collapseByDefault) {
			setExpandedStorageValue(false);
			setExpanded && setExpanded(false);
		} else {
			const storedExpanded = getExpandedStorageValue();

			if (_.isNull(storedExpanded)) {
				setExpanded && setExpanded(true);
			} else {
				setExpanded && setExpanded(storedExpanded);
			}
		}
	}, [isCollapsable, subNavContext, collapseByDefault]);

	const getExpandedStorageValue = (): boolean | null => {
		const storedValue = localStorage.getItem('header-expanded');
		return _.isNull(storedValue) ? null : storedValue === BoolStringEnum.True;
	};

	const setExpandedStorageValue = (collapsed: boolean) => {
		localStorage.setItem('header-expanded', collapsed.toString());
	};

	const backgroundHeaderColor = localStorage.getItem('AppBranding_BackgroundHeaderColor');

	return (
		<Grid>
			<Grid
				span={12}
				style={{
					background: backgroundHeaderColor ?? '#0073c3',
					color: getAccessibleTextColor(backgroundHeaderColor ?? '#002E57'),
				}}
			>
				<Flex direction={{ default: 'row' }}>
					<FlexItem>
						<BreadCrumbs />
					</FlexItem>
					{!pageTitle && isCollapsable && (
						<FlexItem
							onClick={() => {
								onToggle();
							}}
							alignSelf={{ default: 'alignSelfFlexEnd' }}
							align={{ default: 'alignRight' }}
						>
							{isCollapsable && (
								<FontAwesomeIcon
									className="icon"
									icon={expanded ? faAngleUp : faAngleDown}
									size="xl"
								/>
							)}
						</FlexItem>
					)}
				</Flex>
			</Grid>
			<GridItem
				span={12}
				style={{
					background: backgroundHeaderColor ?? '#0073c3',
					color: getAccessibleTextColor(backgroundHeaderColor ?? '#002E57'),
				}}
			>
				{pageTitle && isCollapsable && (
					<Flex direction={{ default: 'row' }}>
						<FlexItem>
							<Title
								headingLevel={'h1'}
								className={'text-secondary sub-heading'}
								style={{
									background: backgroundHeaderColor ?? '#0073c3',
									color: getAccessibleTextColor(
										backgroundHeaderColor ?? '#002E57'
									),
								}}
							>
								{pageTitle}
							</Title>
						</FlexItem>
						<FlexItem
							onClick={() => {
								onToggle();
							}}
							alignSelf={{ default: 'alignSelfFlexEnd' }}
							align={{ default: 'alignRight' }}
						>
							{isCollapsable && (
								<FontAwesomeIcon
									className="icon"
									icon={expanded ? faAngleUp : faAngleDown}
									size="xl"
								/>
							)}
						</FlexItem>
					</Flex>
				)}
			</GridItem>
			<Grid
				span={12}
				className="hidden-when-collapsed"
				style={{
					background: backgroundHeaderColor ?? '#0073c3',
					color: getAccessibleTextColor(backgroundHeaderColor ?? '#002E57'),
				}}
			>
				{subNavContext?.actions && pageDescription ? (
					<GridItem span={12}>
						<FlexItem className="sub-heading-description">
							<Text>{pageDescription}</Text>
						</FlexItem>
					</GridItem>
				) : null}
				<GridItem span={6}>
					{!subNavContext?.actions && pageDescription ? (
						<FlexItem className="sub-heading-description">
							<Text>{pageDescription}</Text>
						</FlexItem>
					) : null}
					{isAnalyze && subNavContext?.analyze && (
						<SubNavCommonActions actionsSummary={subNavContext?.analyze} />
					)}
					{isReports && subNavContext?.reports && (
						<SubNavCommonActions actionsSummary={subNavContext?.reports} />
					)}
					{!isReports && !isAnalyze && subNavContext?.actions && (
						<SubNavCommonActions actionsSummary={subNavContext?.actions} />
					)}
				</GridItem>
				<GridItem span={1} />
				<GridItem span={5}>
					{subNavContext?.report && (
						<SubNavReportSummary reportSummary={subNavContext.report} />
					)}
					{subNavContext?.TipAndTrickPageType && (
						<SubNavTipsAndTricks
							TipAndTrickPageType={subNavContext.TipAndTrickPageType}
						/>
					)}
					{subNavContext?.reportSnapshot && (
						<SubNavReportSnapshot reportSnapshot={subNavContext.reportSnapshot} />
					)}
				</GridItem>
			</Grid>
		</Grid>
	);
}

export default PageTitleSubheader;
